import React from 'react';
import {
  selectIpdRevenueTerms,
  selectInsuranceSettled,
  selectDischargeCounts,
  selectCoverageBanners,
  selectTruncatedMessage,
  selectUnreconciledMessage,
  selectShowPaidAfterDischarge,
} from './monthlyReportModel';
import { DISCHARGE_TYPE_LABELS } from '../../constants/admission';
import './IpdRevenue.css';

// [X2] the 5 DischargeType members + the "UNSET" bucket the board freezes
// `by_type` on — display order only, never a re-derivation of the set.
const DISCHARGE_TYPE_ORDER = ['NORMAL', 'LAMA', 'DAMA', 'DECEASED', 'REFERRED', 'UNSET'];

// MICROCOPY M16 — "UNSET" is legacy rows / TRANSFERRED_OUT admissions moved
// via the generic transition (§6.2); the product genuinely does not know
// the discharge type, so this states that rather than implying a blank
// choice ("Unspecified").
const UNSET_TYPE_LABEL = 'Not recorded';

function Term({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-50">{value}</div>
    </div>
  );
}

/**
 * IpdRevenue (F2 — IPD-MVP discharge-mis, BUILD-PLAN fast-slice-v2 §8
 * WAVE 2; renamed from `IpdRevenueCard` — "Card" is reserved for the
 * dashboard's navigable `ModuleCard` tiles; this renders the IPD Revenue
 * block of the Reports & Accounts report, named after the report it is, not
 * the UI shape it happens to take). The six-term IPD revenue identity + the
 * incomplete/unreconciled tripwire banners. Every rupee figure is read
 * through `monthlyReportModel.js` — nothing here sums or re-derives a total.
 *
 * - Renders ONLY when `data.ipd` is present (§7.0 "render iff key present")
 *   — never a null/zero test.
 * - `insurance_settled` is rendered in its OWN panel, structurally outside
 *   the cash-term grid (C1) — it must never be summed into a cash total.
 * - `concessions` states "Already deducted from Net billed — do not deduct
 *   again" as its own sentence — a reader must not subtract it again.
 * - `still_outstanding` is labelled "net of write-offs" ([X4] — the wire
 *   value already has written-off amounts removed; this label is what keeps
 *   a reader from double-subtracting them).
 * - `coverage.truncated` → a red incomplete strip stating export is
 *   disabled and why. Non-empty `coverage.unreconciled` → the count stated
 *   plainly, in the frozen copy from §7.1's scope note. Neither banner
 *   renders/disables the actual Export button (`ExportMonthButton`, F5) —
 *   both read the SAME `monthlyReportModel` selector so the two surfaces
 *   can't disagree.
 * - No OPD+IPD combined total anywhere (C8) — there is nothing to combine
 *   yet, and this component never introduces one.
 *
 * DOCTOR-REPORTS P1 (FE-2) — two ADDITIVE optional props:
 *   - `doctorBucket` — a `selectDoctorBuckets` entry (or null/undefined for
 *     "All doctors"). When given, every money/discharge figure reads the
 *     BUCKET instead of `data.ipd` — coverage banners and the basis note
 *     stay org-level regardless (they describe the read, not the filter).
 *   - `showPaidAfterDischarge` — when omitted, derived from the ORG term
 *     (`selectShowPaidAfterDischarge(data.ipd)`, D3) so the tile never
 *     flickers as the doctor filter changes.
 * Without `doctorBucket` the render is byte-identical to before apart from
 * the "Dues collected (cash received)" → "Paid after discharge" rename.
 *
 * DOCTOR-REPORTS P2 (FE-4) — `hasOpd` (additive, default false): when the
 * board carries the new cash blocks, a pointer sentence is appended after
 * `basis_note` so a reader of the (unchanged, server-owned) "laboratory
 * money is reported against the laboratory visit" line knows where that
 * money actually shows up now.
 */
export default function IpdRevenue({ data, doctorBucket = null, showPaidAfterDischarge, hasOpd = false }) {
  const hasIpd = !!(data && Object.prototype.hasOwnProperty.call(data, 'ipd'));
  if (!hasIpd) return null;

  const figuresSource = doctorBucket || data.ipd;
  const terms = selectIpdRevenueTerms(figuresSource);
  // QA-5 / bk-463 — `hasIpd` only proves the key is PRESENT; a present-but-
  // `null` `ipd` (unreachable today, §7.0 forbids it and the ONE producer
  // enforces it via `response_model_exclude_none=True`) would otherwise
  // TypeError on `terms.netBilled` below and white-screen the report.
  if (!terms) return null;
  const insurance = selectInsuranceSettled(figuresSource);
  const discharges = selectDischargeCounts(figuresSource);
  const coverage = selectCoverageBanners(data.coverage);
  const periodLabel = data.period?.label;
  const showPaid = showPaidAfterDischarge !== undefined
    ? showPaidAfterDischarge
    : selectShowPaidAfterDischarge(data.ipd);
  const doctorLabelText = doctorBucket?.label || null;

  return (
    <div
      data-testid="ipd-revenue"
      className="ipd-revenue rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">IPD Revenue</h2>
        {discharges && (
          <span data-testid="discharge-count" className="text-xs text-slate-500 dark:text-slate-400">
            {discharges.total === 0
              ? `No discharges in ${periodLabel}${doctorLabelText ? ` for ${doctorLabelText}` : ''}`
              : `${discharges.total} discharge${discharges.total === 1 ? '' : 's'} in ${periodLabel}${doctorLabelText ? ` for ${doctorLabelText}` : ''}`}
          </span>
        )}
      </div>

      {doctorLabelText && (
        <p data-testid="doctor-filter-notice" className="mb-3 text-xs text-teal-700 dark:text-teal-300">
          Showing figures for {doctorLabelText} only. Choose &quot;All doctors&quot; to see the whole organisation.
        </p>
      )}

      {data.basis_note && (
        <p data-testid="ipd-basis-note" className="mb-3 text-xs text-slate-500 dark:text-slate-400">
          {data.basis_note}
          {hasOpd && (
            <span data-testid="ipd-lab-pointer">
              {' '}Lab and radiology tests for admitted patients are shown on the &quot;OPD, OTC, Lab &amp; Diagnostic&quot; tab.
            </span>
          )}
        </p>
      )}

      {coverage.truncated && (
        <div
          role="alert"
          data-testid="incomplete-strip"
          className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
        >
          {selectTruncatedMessage(coverage.scanned, periodLabel)}
        </div>
      )}

      {coverage.hasUnreconciled && (
        <div
          role="alert"
          data-testid="unreconciled-banner"
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
        >
          {selectUnreconciledMessage(coverage.unreconciledCount, periodLabel)}
        </div>
      )}

      <div data-testid="ipd-cash-terms" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Term testId="term-net-billed" label="Net billed" value={terms.netBilled} />
        <Term testId="term-deposits-adjusted" label="Deposits adjusted (after refunds)" value={terms.depositsAdjusted} />
        {showPaid && (
          <Term testId="term-paid-after-discharge" label="Paid after discharge" value={terms.duesCollected} />
        )}
        <Term testId="term-written-off" label="Written off (bad debt)" value={terms.writtenOff} />
        <Term
          testId="term-still-outstanding"
          label="Still outstanding (net of write-offs)"
          value={terms.stillOutstanding}
        />
      </div>

      {/* Non-cash accrual — deliberately OUTSIDE `ipd-cash-terms` (C1). */}
      <div
        data-testid="insurance-settled-panel"
        className="mt-2 rounded-lg border border-indigo-400 bg-indigo-50 p-3 dark:border-indigo-600 dark:bg-slate-800"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-indigo-700 dark:text-indigo-300">Insurance settled (non-cash)</span>
          <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">{insurance.insuranceSettled}</span>
        </div>
        <p data-testid="insurance-settled-caption" className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
          Recognised when the bill was settled against the insurer. This report does not track whether
          the insurer has paid.
        </p>
      </div>

      <div data-testid="concessions-memo" className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Concessions: {terms.concessions}. Already deducted from Net billed — do not deduct again.
      </div>

      {discharges && discharges.total > 0 && (
        <div data-testid="discharge-by-type" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          {DISCHARGE_TYPE_ORDER.filter((t) => discharges.byType[t] !== undefined).map((t) => (
            <span key={t} data-testid={`discharge-type-${t}`}>
              {(DISCHARGE_TYPE_LABELS[t] || UNSET_TYPE_LABEL)}: {discharges.byType[t]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
